import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import ProductCover from '../../shared/common/ProductCover';
import Utils from '../../shared/utils';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { useTranslation } from 'react-i18next';
import { ProductMetaData } from '../../../models/Product';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../shared/utils/togglz';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';

interface Props {
    product: ProductMetaData;
    rank: number;
    shopLanguage: string | undefined;
}

const TopChartRow = ({ product, rank, shopLanguage }: Props) => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    if (!product) {
        return null;
    }

    const highRank = 3;
    const title = `${rank}. ${product.titleWithoutPrefix}`;
    const firstAuthor = product.authors && product.authors.length > 0 ? product.authors[0] : undefined;
    const author = firstAuthor
        ? `${firstAuthor?.firstName ? firstAuthor.firstName : ''} ${firstAuthor?.lastName}`
        : undefined;
    const productLocation = `/products/${product.id}`;
    const inlineStyle = { display: 'inline-block', marginRight: '5px' };

    const rowClass = classNames('chart-row', {
        highrank: rank <= highRank,
        lowrank: rank > highRank
    });
    const copyStockClass = product.hasOwnProperty('copyStockAmount') ? '' : 'copy-stock-warning';
    const reserveAmountClasses = product.hasOwnProperty('reserveAmount') ? 'copy-stock-warning' : '';

    const copyStockInner = (
        <span
            className={copyStockClass}
            style={inlineStyle}
            title={t('components.productStatistics.copiesInYourLibrary')}
        >
            <i className="material-icons fl-left smaller" style={{ marginRight: '1px' }}>
                content_copy
            </i>
            {product.hasOwnProperty('copyStockAmount') ? product.copyStockAmount : 0}
        </span>
    );

    const reserveAmountInner =
        product.reserveAmount && product.copyStockAmount && product.reserveAmount / product.copyStockAmount > 2 ? (
            <span className={reserveAmountClasses} style={inlineStyle} title="Reserve Amount">
                <i className="material-icons fl-left smaller" style={{ marginRight: '1px' }}>
                    person
                </i>
                {product.hasOwnProperty('reserveAmount') ? product.reserveAmount : 0}
            </span>
        ) : null;

    return (
        <div className={rowClass}>
            <Link to={productLocation}>
                <ProductCover product={product} />
            </Link>
            <div className="product-meta">
                <Link className="tr-title" style={inlineStyle} to={productLocation}>
                    {title}
                </Link>
                {rank > highRank ? copyStockInner : null}
                {rank > highRank ? reserveAmountInner : null}
                {firstAuthor && author ? (
                    <Link
                        className="product-author"
                        to={Utils.catalogueLocationDescriptorString({
                            loanFormat: mapLoanFormat(product.loanFormat),
                            filter: { author: { include: [firstAuthor.id] } },
                            isMultiLanguageShopEnabled,
                            shopLanguage,
                            ageCategory: ageCategorySearchParam
                        })}
                    >
                        <div>{author}</div>
                    </Link>
                ) : null}
                {rank <= highRank ? copyStockInner : null}
                {rank <= highRank ? reserveAmountInner : null}
            </div>
        </div>
    );
};

TopChartRow.propTypes = {
    product: PropTypes.object.isRequired,
    rank: PropTypes.number.isRequired
};

export default TopChartRow;
